import { listingFromProduct } from '@guildite/domain/listings';
import type { Promo } from '@guildite/domain/promos';

import { visibleListings } from './blocks.ts';
import type { BlockFilter, ListingsPort } from './ports.ts';
import type { WorkerClient } from './worker.ts';

type Reader = (collection: string, options: { filters?: [string, '==', unknown][]; orderBy?: [string, 'asc' | 'desc']; limit?: number }) => Promise<{ id: string; data: Record<string, unknown> }[]>;

// A shop that blocked you shows no listings (data/blocks.ts); its team is
// never blocked by it, so their own manage screens are unaffected.
export function createListings({ worker, query, readDocument, hidden }: {
  hidden?: () => Promise<BlockFilter>;
  worker: WorkerClient;
  query: Reader;
  readDocument: (collection: string, id: string) => Promise<Record<string, unknown> | null>;
}): ListingsPort {
  return {
    async forShop(shopId, { includeHidden = false } = {}) {
      const rows = await query('products', { filters: [['shopId', '==', shopId]], orderBy: ['updatedAt', 'desc'], limit: 200 });
      const listings = rows.map(row => listingFromProduct(row.id, row.data)).filter(listing => includeHidden || listing.active);
      return hidden ? visibleListings(listings, await hidden()) : listings;
    },
    async get(listingId) {
      const product = await readDocument('products', listingId);
      if (!product) return null;
      const listing = listingFromProduct(listingId, product);
      return hidden && (await hidden()).shops.has(listing.shopId) ? null : listing;
    },
    save: (shopId, listingId, input, video) => worker.call('save_shop_listing', { shopId, ...(listingId ? { listingId } : {}), listing: input, ...(video !== undefined ? { video } : {}) }),
    setActive: async (shopId, listingId, active) => { await worker.call('set_listing_active', { shopId, listingId, active }); },
    remove: async (shopId, listingId) => { await worker.call('delete_shop_listing', { shopId, listingId }); },
    promos: async shopId => (await worker.call<{ promos: Promo[] }>('list_shop_promos', { shopId })).promos,
    savePromo: async (shopId, promo, mode = 'create') => (await worker.call<{ promo: Promo }>('save_shop_promo', { shopId, promo, mode })).promo,
    setPromoActive: async (shopId, code, active) => { await worker.call('set_shop_promo_active', { shopId, code, active }); },
    removePromo: async (shopId, code) => { await worker.call('set_shop_promo_active', { shopId, code, remove: true }); },
    checkPromo: (shopId, code, lines) => worker.call('check_promo', { shopId, code, lines }),
    stayAvailability: async listingId => (await worker.call<{ booked: string[] }>('stay_availability', { listingId })).booked,
  };
}
